import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "manager-requests" }

import { ref, watch } from 'vue';
import type { Request, RequestStatus, UpdateStatusPayload } from './types';
import { useRequestData } from './composables/useRequestData';
import RequestsList from './components/RequestsList.vue';
import RequestDetail from './components/RequestDetail.vue';
import ReplyForm from './components/ReplyForm.vue';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const selectedStatus = ref<RequestStatus | ''>('');
const selectedRequest = ref<Request | null>(null);
const isDetailOpen = ref(false);
const isReplyOpen = ref(false);
const replyMessage = ref('');
const successDialogVisible = ref(false);
const successMessage = ref('');
const errorDialogVisible = ref(false);
const errorDialogMessage = ref('');
const replyLoading = ref(false);
const replyFormRef = ref();
const requestDetailRef = ref();

const {
  requests,
  loading,
  error,
  loadRequests,
  loadRequestDetails,
  updateStatus,
  sendReply,
  uploadFiles,
  deleteRequest,
} = useRequestData();

const confirm = useConfirm();

watch(error, (err) => {
  if (err) {
    errorDialogMessage.value = err;
    errorDialogVisible.value = true;
  }
});

function handleStatusFilterChange(status: RequestStatus | '') {
  selectedStatus.value = status;
  const filter = status ? [status] : [];
  loadRequests({ status: filter });
}

async function openRequest(request: Request): Promise<void> {
  const fullRequest = await loadRequestDetails(request.id);
  if (fullRequest) {
    selectedRequest.value = fullRequest;
    isDetailOpen.value = true;
  } else {
    errorDialogMessage.value = 'Не удалось загрузить детали заявки';
    errorDialogVisible.value = true;
  }
}

function closeDetail(): void {
  isDetailOpen.value = false;
  selectedRequest.value = null;
}

function onReplyClick(request: Request): void {
  selectedRequest.value = request;
  isDetailOpen.value = false;
  isReplyOpen.value = true;
  replyMessage.value = '';
}

function onReplyModalClose(visible: boolean): void {
  if (!visible) {
    if (replyFormRef.value) {
      replyFormRef.value.clearMessage();
    }
    replyMessage.value = '';
  }
}

async function submitReplyFromModal() {
  if (!selectedRequest.value || !replyMessage.value.trim()) return;

  try {
    replyLoading.value = true;
    await sendReply(selectedRequest.value.id, { message: replyMessage.value });

    successMessage.value = 'Ответ отправлен пользователю';
    successDialogVisible.value = true;
    isReplyOpen.value = false;
    await loadRequests({ status: selectedStatus.value ? [selectedStatus.value] : [] });
  } catch (err) {
    // Ошибки обрабатываются через watch(error)
  } finally {
    replyLoading.value = false;
  }
}

function onSuccessDialogClose(visible: boolean): void {
  if (!visible) {
    if (selectedRequest.value) {
      isDetailOpen.value = true;
    }
  }
}

async function onStatusUpdated(status: RequestStatus): Promise<void> {
  if (!selectedRequest.value) return;
  try {
    await updateStatus(selectedRequest.value.id, { status });
    successMessage.value = 'Статус заявки обновлён';
    successDialogVisible.value = true;
    const updatedRequest = await loadRequestDetails(selectedRequest.value.id);
    if (updatedRequest) {
      selectedRequest.value = updatedRequest;
    }
  } catch (err) {
    console.error('Ошибка обновления статуса:', err);
  }
}

async function onFilesUploaded(files: File[]): Promise<void> {
  if (!selectedRequest.value) return;
  try {
    const result = await uploadFiles(selectedRequest.value.id, files);

    if (result?.success) {
      const updatedRequest = await loadRequestDetails(selectedRequest.value.id);
      if (updatedRequest) {
        selectedRequest.value = updatedRequest;
        // Очищаем форму файлов после успешной загрузки
        if (requestDetailRef.value) {
          requestDetailRef.value.clearAttachedFiles();
        }
      }
    } else {
      errorDialogMessage.value = result?.msg || 'Не удалось загрузить файлы';
      errorDialogVisible.value = true;
    }
  } catch (err) {
    errorDialogMessage.value = err instanceof Error ? err.message : 'Неизвестная ошибка';
    errorDialogVisible.value = true;
  }
}

async function onDeleteRequest(id: number): Promise<void> {
  confirm.require({
    message: 'Вы уверены, что хотите удалить заявку и всю переписку?',
    header: 'Подтверждение удаления',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    acceptClass: 'p-button-danger',
    rejectClass: 'p-button-secondary',
    accept: async () => {
      try {
        await deleteRequest(id);
        loadRequests({ status: selectedStatus.value ? [selectedStatus.value] : [] });
      } catch (err) {
        console.error('Ошибка удаления заявки:', err);
        loadRequests({ status: selectedStatus.value ? [selectedStatus.value] : [] });
      }
    },
    reject: () => {},
  });
}

function onReplyFromDetail() {
  isDetailOpen.value = false;
  isReplyOpen.value = true;
  replyMessage.value = '';
}

return (_ctx: any,_cache: any) => {
  const _component_Dialog = _resolveComponent("Dialog")!
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(RequestsList, {
      requests: _unref(requests),
      loading: _unref(loading),
      "selected-status": selectedStatus.value,
      "onUpdate:selectedStatus": handleStatusFilterChange,
      onRowClick: openRequest,
      onDeleteRequest: onDeleteRequest,
      onReply: onReplyClick
    }, null, 8, ["requests", "loading", "selected-status"]),
    _createVNode(_component_Dialog, {
      visible: isDetailOpen.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((isDetailOpen).value = $event)),
      modal: "",
      maximizable: "",
      style: { width: '90vw', maxWidth: '1200px', height: '90vh' },
      draggable: false,
      header: `Заявка #${selectedRequest.value?.id || ''}`
    }, {
      default: _withCtx(() => [
        (selectedRequest.value)
          ? (_openBlock(), _createBlock(RequestDetail, {
              key: 0,
              ref_key: "requestDetailRef",
              ref: requestDetailRef,
              request: selectedRequest.value,
              onClose: closeDetail,
              onStatusUpdated: onStatusUpdated,
              onFilesUploaded: onFilesUploaded,
              loading: _unref(loading),
              onReply: onReplyFromDetail
            }, null, 8, ["request", "loading"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["visible", "header"]),
    _createVNode(_component_Dialog, {
      visible: isReplyOpen.value,
      "onUpdate:visible": [
        _cache[2] || (_cache[2] = ($event: any) => ((isReplyOpen).value = $event)),
        onReplyModalClose
      ],
      modal: "",
      style: { width: '90vw', maxWidth: '800px' },
      draggable: false,
      header: `Ответ на заявку #${selectedRequest.value?.id || ''}`
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_Button, {
          label: replyLoading.value ? 'Отправка...' : 'Отправить',
          icon: replyLoading.value ? 'pi pi-spin pi-spinner' : 'pi pi-send',
          disabled: !replyMessage.value.trim() || replyLoading.value,
          onClick: submitReplyFromModal
        }, null, 8, ["label", "icon", "disabled"])
      ]),
      default: _withCtx(() => [
        (selectedRequest.value)
          ? (_openBlock(), _createBlock(ReplyForm, {
              key: 0,
              ref_key: "replyFormRef",
              ref: replyFormRef,
              modelValue: replyMessage.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((replyMessage).value = $event)),
              loading: replyLoading.value
            }, null, 8, ["modelValue", "loading"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["visible", "header"]),
    _createVNode(_component_Dialog, {
      visible: successDialogVisible.value,
      "onUpdate:visible": [
        _cache[4] || (_cache[4] = ($event: any) => ((successDialogVisible).value = $event)),
        onSuccessDialogClose
      ],
      modal: "",
      style: { width: '30rem' },
      draggable: false
    }, {
      header: _withCtx(() => [...(_cache[6] || (_cache[6] = [
        _createTextVNode("Успешно", -1)
      ]))]),
      footer: _withCtx(() => [
        _createVNode(_component_Button, {
          label: "OK",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (successDialogVisible.value = false))
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", null, _toDisplayString(successMessage.value), 1)
      ]),
      _: 1
    }, 8, ["visible"]),
    _createVNode(_unref(ConfirmDialog)),
    _createVNode(_component_Dialog, {
      visible: errorDialogVisible.value,
      "onUpdate:visible": _cache[5] || (_cache[5] = ($event: any) => ((errorDialogVisible).value = $event)),
      modal: "",
      style: { width: '30rem' },
      draggable: false
    }, {
      header: _withCtx(() => [...(_cache[7] || (_cache[7] = [
        _createTextVNode("Ошибка", -1)
      ]))]),
      default: _withCtx(() => [
        _createElementVNode("p", null, _toDisplayString(errorDialogMessage.value), 1)
      ]),
      _: 1
    }, 8, ["visible"])
  ]))
}
}

})